import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PROGRAMIZ } from './programizTheme';
import {
  extensionForLanguage,
  suggestedFileName,
} from './localFileManager';

export const SAVE_TYPE_OPTIONS = [
  { id: 'c', label: 'C Source (*.c)', extension: '.c' },
  { id: 'cpp', label: 'C++ Source (*.cpp)', extension: '.cpp' },
  { id: 'java', label: 'Java (*.java)', extension: '.java' },
  { id: 'python', label: 'Python (*.py)', extension: '.py' },
  { id: 'html', label: 'HTML (*.html)', extension: '.html' },
  { id: 'css', label: 'CSS (*.css)', extension: '.css' },
  { id: 'javascript', label: 'JavaScript (*.js)', extension: '.js' },
];

function withExtension(name, extension) {
  const base = String(name || 'file').replace(/\.[^.]+$/, '').trim() || 'file';
  return `${base}${extension}`;
}

/**
 * Fallback Save As UI for browsers without File System Access API (e.g. Firefox).
 * Windows folder picker is only available in Chrome / Edge.
 */
export default function SaveAsFallbackDialog({
  open,
  language,
  fileName,
  onCancel,
  onConfirm,
}) {
  const defaultExt = extensionForLanguage(language);
  const initialType =
    SAVE_TYPE_OPTIONS.find((t) => t.extension === defaultExt)?.id || 'html';

  const [typeId, setTypeId] = useState(initialType);
  const [nameInput, setNameInput] = useState(
    suggestedFileName(language, fileName),
  );

  useEffect(() => {
    if (!open) return;
    setTypeId(initialType);
    setNameInput(suggestedFileName(language, fileName));
  }, [open, initialType, language, fileName]);

  const selectedType = useMemo(
    () => SAVE_TYPE_OPTIONS.find((t) => t.id === typeId) || SAVE_TYPE_OPTIONS[0],
    [typeId],
  );

  const finalName = withExtension(nameInput, selectedType.extension);

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 18, fontWeight: 600 }}>Save As</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Folder choose cheyalante Chrome or Edge use cheyandi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Firefox lo Windows Save As (Desktop / Documents choose) support
              undadu. File Downloads folder ki download avutundi. Location
              pick cheyali ante ee page ni{' '}
              <Box component="span" sx={{ fontWeight: 600 }}>
                Google Chrome
              </Box>{' '}
              or{' '}
              <Box component="span" sx={{ fontWeight: 600 }}>
                Microsoft Edge
              </Box>{' '}
              lo open cheyandi.
            </Typography>
          </Alert>

          <TextField
            label="File name"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            onFocus={(e) => e.target.select()}
          />

          <TextField
            select
            label="Save as type"
            value={typeId}
            onChange={(e) => {
              const nextId = e.target.value;
              const next = SAVE_TYPE_OPTIONS.find((t) => t.id === nextId);
              setTypeId(nextId);
              if (next) setNameInput((prev) => withExtension(prev, next.extension));
            }}
            fullWidth
            size="small"
          >
            {SAVE_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <Typography variant="caption" sx={{ color: PROGRAMIZ.muted }}>
            Will download as: <strong>{finalName}</strong>
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm?.(finalName)}
          sx={{ bgcolor: PROGRAMIZ.tabActive, '&:hover': { bgcolor: PROGRAMIZ.runHover } }}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}
